
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OrganizationManagement } from '@/components/superadmin/OrganizationManagement';
import { GlobalUserManagement } from '@/components/superadmin/GlobalUserManagement';
import { ImportedUsersManagement } from '@/components/superadmin/ImportedUsersManagement';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Users, Shield, FileText, Mail, CreditCard } from 'lucide-react';
import { CommissionRatesTable } from '@/components/payment/CommissionRatesTable';
import { PaymentDashboard } from '@/components/payment/PaymentDashboard';
import { TransactionHistoryTable } from '@/components/payment/TransactionHistoryTable';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const SuperAdmin = () => {
  const { profile, loading, isImpersonating, effectiveRole } = useAuth();

  // Fetch import counts for badges
  const { data: importRecords } = useQuery({
    queryKey: ['user-imports-counts'],
    queryFn: async () => {
      const { data: imports } = await supabase
        .from('user_imports')
        .select('status')
        .order('created_at', { ascending: false });
      return imports || [];
    },
    enabled: effectiveRole === 'superadmin' && !isImpersonating,
  });

  const readyToSendCount = importRecords?.filter(r => r.status === 'imported').length || 0;
  const invitedCount = importRecords?.filter(r => r.status === 'invited').length || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect if not superadmin or if currently impersonating
  if (!profile || effectiveRole !== 'superadmin' || isImpersonating) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8 text-red-600" />
            Super Administration
          </h1>
          <p className="text-muted-foreground mt-2">
            Global system management across all organizations
          </p>
        </div>

        <div className="mb-6 flex gap-3">
          <Link to="/superadmin/emails">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Manage Email Templates
            </Button>
          </Link>
          <Link to="/superadmin/payment-config">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              Payment Configuration
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="organizations" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="organizations" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organizations
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Members
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payment Overview
            </TabsTrigger>
            <TabsTrigger value="imports" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Import Management
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organizations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Organization Management
                </CardTitle>
                <CardDescription>
                  Create and manage organizations across the entire system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrganizationManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Member Management
                </CardTitle>
                <CardDescription>
                  Manage all members across organizations and assign roles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GlobalUserManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Platform Payment Overview
                  </CardTitle>
                  <CardDescription>
                    Overview of all payment activity across the platform
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PaymentDashboard />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>All Platform Transactions</CardTitle>
                  <CardDescription>
                    Complete transaction history across all organizations and clubs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TransactionHistoryTable />
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          <TabsContent value="imports">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Import Management
                    </CardTitle>
                    <CardDescription>
                      Process CSV imported users and manage import records
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-blue-600">
                      {readyToSendCount} Imported
                    </Badge>
                    <Badge variant="outline" className="text-green-600">
                      {invitedCount} Invited
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ImportedUsersManagement />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SuperAdmin;
